import React from 'react';
import './StayPanel.scss';
import { SharedUtils, TranslationUtils } from '../../utils';
import { Guests, Stay, HomeInfo, AdjustmentPolicies } from 'guest-data-types-ts-lib';

import moment from 'moment';
import { CheckDateTypes } from './StayPanel';
import { FormattedDate } from 'react-intl';
import { OccupancyCounts } from '../../types/occupancy';
import { ExtendStayPreviewRequest, TripAdjustmentPreviewResponse, TripAdjustmentResponse } from 'guest-data-types-ts-lib/types/adjustments';

interface StayPanelReservationDetailsProps {
    occupancy_details?: { initialOccupancy?: OccupancyCounts | null; occupancyCounts?: OccupancyCounts | null };
    preview: TripAdjustmentResponse | null;
    home_info: HomeInfo;
    guests: Guests;
    stay: Stay;
}

export const StayPanelReservationDetails: React.FC<StayPanelReservationDetailsProps> = (props) => {
    const { home_info, guests, stay, preview, occupancy_details } = props;

    let adjustmentName: AdjustmentPolicies | undefined;

    if (preview) {
        const { adjustment }: any = preview;
        adjustmentName = adjustment.name as AdjustmentPolicies;
    }

    const renderInfoGuests = (): JSX.Element | undefined => {
        let currentAdult = 0,
            currentChild = 0,
            currentPet = 0;
        const { current_counts } = guests;
        currentAdult = current_counts.adults;
        currentChild = current_counts.children;
        currentPet = current_counts.pets;
        switch (adjustmentName) {
            case 'addNight' || 'tripAddNight':
                return renderInfoGuestsChild(currentAdult, currentChild, currentPet);
            case 'adjustOccupancyAndPets':
                // @ts-ignore
                const { occupancyCounts } = occupancy_details;
                currentAdult = occupancyCounts?.adults.count;
                currentChild = occupancyCounts?.children.count;
                currentPet = occupancyCounts?.pets?.count;
                return renderInfoGuestsChild(currentAdult, currentChild, currentPet);
            default:
                return renderInfoGuestsChild(currentAdult, currentChild, currentPet);
        }
    };

    const renderInfoGuestsChild = (currentAdult: number, currentChild: number, currentPet: number): JSX.Element => {
        let infoPet = '',
            infoAdult = '',
            infoChild = '',
            infoGuests;

        let allowsPets = home_info.allows_dogs || false;
        if (currentAdult > 0) {
            infoAdult = SharedUtils.getSlugOccupancy('adults', currentAdult);
        }
        if (currentChild > 0) {
            infoChild = `, ${SharedUtils.getSlugOccupancy('children', currentChild)}`;
        }
        if (currentPet > 0) {
            if (allowsPets) {
                infoPet = `, ${SharedUtils.getSlugOccupancy('pets', currentPet)}`;
            }
        }
        infoGuests = `${infoAdult} ${infoChild} ${infoPet}`;
        return <div className={'body-regular'}>{infoGuests}</div>;
    };

    const getDate = (checkType: CheckDateTypes): moment.Moment | string => {
        switch (adjustmentName) {
            case 'addNight':
            case 'tripAddNight':
                if (checkType === CheckDateTypes.CHECK_IN) {
                    return stay.check_in_date_time;
                }
                return moment(stay.check_out_date_time).add(1, 'day').toString();
            case 'extendedStay':
                if (checkType === CheckDateTypes.CHECK_IN) {
                    const first_night = (preview?.adjustment as ExtendStayPreviewRequest).data.first_night;
                    return moment(first_night).toString();
                }
                const last_night = (preview?.adjustment as ExtendStayPreviewRequest).data.last_night;
                return moment(last_night).add(1, 'days').toString();
            default:
                if (checkType === CheckDateTypes.CHECK_IN) {
                    return stay.check_in_date_time;
                }
                return moment(stay.check_out_date_time).toString();
        }
    };

    const getCheckOutTime = (lateCheckOutTime: string): moment.Moment | string => {
        switch (adjustmentName) {
            case 'addNight':
            case 'tripAddNight':
                return moment(`${stay.check_out_date} ${lateCheckOutTime}`).add(1, 'day');
            case 'extendedStay':
                const last_night = (preview?.adjustment as ExtendStayPreviewRequest).data.last_night;
                return moment(`${last_night} ${lateCheckOutTime}`);
            case 'lateCheckout':
                const {
                    data: { check_out_date, check_out_date_time },
                } = preview?.adjustment as any;
                return moment(`${check_out_date} ${check_out_date_time}`);
            default:
                return moment(`${stay.check_out_date} ${lateCheckOutTime}`);
        }
    };

    const renderDates = (checkType: CheckDateTypes): JSX.Element => {
        const date: any = getDate(checkType);
        let time;

        if (checkType === CheckDateTypes.CHECK_IN) {
            time = moment(`${stay.check_in_date} ${stay.check_in_time}`);
        } else {
            const checkOutTime = stay.late_check_out_purchased ? stay.late_check_out_time : stay.check_out_time;
            time = getCheckOutTime(checkOutTime);
        }

        return (
            <div>
                <div className="body-regular">
                    <FormattedDate value={new Date(date)} month="short" day="numeric" weekday="short" />
                </div>
                <div className="body-regular">{moment(time).format('LT')}</div>
            </div>
        );
    };

    return (
        <div className={'booking-details type-body mt-16'}>
            <div className={'d-flex dates position-relative pb-16 px-0'}>
                <div className={'col-6 text-left px-0 pr-16'}>
                    <div tabIndex={0} className={'body-small-bold  mt-16'}>
                        {'Check-In'}
                    </div>
                    <div tabIndex={0}>{renderDates(CheckDateTypes.CHECK_IN)}</div>
                </div>
                <div className={'col-6 text-right px-0 pl-16'}>
                    <div tabIndex={0} className={'body-small-bold   mt-16'}>
                        {'Check-Out'}
                    </div>
                    <div className={'body-regular'} tabIndex={0}>
                        {renderDates(CheckDateTypes.CHECK_OUT)}
                    </div>
                </div>
                <div className={'svg-positioner'}>
                    <svg className="arrow-stay" width="20" height="100">
                        <line x1="0" y1="0" x2="20" y2="50" />
                        <line x1="20" y1="50" x2="0" y2="100" />
                    </svg>
                </div>
            </div>
            {adjustmentName !== 'poolHeat' && (
                <div className={`guests pb-16 ${adjustmentName === 'extendedStay' ? 'd-none' : ''}`}>
                    <div tabIndex={0}>
                        <h3 className="body-small-bold mt-16">{TranslationUtils.get('guests')}</h3>
                    </div>
                    <div tabIndex={0}>{renderInfoGuests()}</div>
                </div>
            )}
            {adjustmentName === 'extendedStay' && <div className={'line'} />}
            {adjustmentName === 'poolHeat' && <div className={'line'} />}
        </div>
    );
};
